import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Info, Loader2 } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/ui/tooltip";
import type { CreateCertificateRequestPayload } from "@/services/certificateRequestsService";

const CERTIFICADOS_EMAIL = "certificados@fiscalo.uy";

export interface FiscaloCertificateConfirmCardProps {
  /** Payload that will be sent to create the certificate request */
  certificateRequestPayload: CreateCertificateRequestPayload;
  /** User's document number (CI or passport) */
  documentNumber: string | null;
  /** User's document type for display */
  documentType: "national_id" | "passport" | null;
  /** Company RUT */
  rut: string | null;
  /** Company legal name */
  legalName: string | null;
  /** Company trade name */
  tradeName: string | null;
  /** Default phone (e.g. tenant.phone); user can edit */
  defaultPhone: string | null;
  onConfirm: (
    phone: string | undefined,
    tradeName: string | undefined,
  ) => Promise<"submitted" | "failed" | void>;
  confirming?: boolean;
}

export function FiscaloCertificateConfirmCard({
  certificateRequestPayload,
  documentNumber,
  documentType,
  rut,
  legalName,
  tradeName,
  defaultPhone,
  onConfirm,
  confirming = false,
}: FiscaloCertificateConfirmCardProps): JSX.Element {
  const { t } = useTranslation();
  const [phone, setPhone] = useState(defaultPhone?.trim() ?? "");
  const [tradeNameValue, setTradeNameValue] = useState(
    (tradeName ?? legalName ?? "").trim()
  );

  const documentTypeLabel =
    documentType === "passport"
      ? t("onboarding.certificateAcquisition.fiscaloConfirm.documentTypePassport")
      : t("onboarding.certificateAcquisition.fiscaloConfirm.documentTypeCi");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void onConfirm(
      phone.trim() || undefined,
      tradeNameValue.trim() || undefined
    );
  };

  return (
    <Card>
      <CardContent className="pt-6 space-y-6">
        <div className="rounded-lg border border-border bg-muted/30 divide-y divide-border">
          <Row
            label={t("onboarding.certificateAcquisition.fiscaloConfirm.documentType")}
            value={documentTypeLabel}
          />
          <Row
            label={t("onboarding.certificateAcquisition.fiscaloConfirm.documentNumber")}
            value={documentNumber ?? "—"}
          />
          <Row
            label={t("onboarding.certificateAcquisition.fiscaloConfirm.rut")}
            value={rut ?? "—"}
          />
          <Row
            label={t("onboarding.certificateAcquisition.fiscaloConfirm.legalName")}
            value={legalName ?? tradeName ?? "—"}
          />
          <div className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
            <Label
              htmlFor="fiscalo-confirm-tradeName"
              className="text-xs sm:text-sm text-muted-foreground shrink-0 sm:w-40"
            >
              {t("onboarding.certificateAcquisition.fiscaloConfirm.tradeName")}
            </Label>
            <Input
              id="fiscalo-confirm-tradeName"
              type="text"
              value={tradeNameValue}
              onChange={(e) => setTradeNameValue(e.target.value)}
              placeholder={t("onboarding.certificateAcquisition.fiscaloConfirm.tradeNamePlaceholder")}
              className="flex-1 text-sm font-medium transition-colors duration-150"
              disabled={confirming}
            />
          </div>
          <Row
            label={t("onboarding.certificateAcquisition.fiscaloConfirm.firstName")}
            value={certificateRequestPayload.firstName}
          />
          {certificateRequestPayload.secondName && (
            <Row
              label={t("onboarding.certificateAcquisition.fiscaloConfirm.secondName")}
              value={certificateRequestPayload.secondName}
            />
          )}
          <Row
            label={t("onboarding.certificateAcquisition.fiscaloConfirm.firstLastName")}
            value={certificateRequestPayload.firstLastName}
          />
          {certificateRequestPayload.secondLastName && (
            <Row
              label={t("onboarding.certificateAcquisition.fiscaloConfirm.secondLastName")}
              value={certificateRequestPayload.secondLastName}
            />
          )}
          <div className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
            <span className="text-xs sm:text-sm text-muted-foreground shrink-0 sm:w-40">
              {t("onboarding.certificateAcquisition.fiscaloConfirm.email")}
            </span>
            <span className="flex-1 flex items-center gap-2 text-sm font-medium text-foreground break-words">
              {CERTIFICADOS_EMAIL}
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger
                    type="button"
                    className="shrink-0 rounded-full p-0.5 text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 transition-colors"
                    aria-label={t("onboarding.certificateAcquisition.fiscaloConfirm.emailLegend")}
                  >
                    <Info className="h-4 w-4" />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-[280px]">
                    {t("onboarding.certificateAcquisition.fiscaloConfirm.emailLegend")}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </span>
          </div>
          <div className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
            <Label
              htmlFor="fiscalo-confirm-phone"
              className="text-xs sm:text-sm text-muted-foreground shrink-0 sm:w-40"
            >
              {t("onboarding.certificateAcquisition.fiscaloConfirm.phoneLabel")}
            </Label>
            <Input
              id="fiscalo-confirm-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder={t("onboarding.certificateAcquisition.fiscaloConfirm.phonePlaceholder")}
              className="flex-1 text-sm font-medium transition-colors duration-150"
              disabled={confirming}
            />
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-row justify-end pt-2">
          <Button type="submit" disabled={confirming}>
            {confirming ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("onboarding.certificateAcquisition.fiscaloConfirm.confirming")}
              </>
            ) : (
              t("onboarding.certificateAcquisition.fiscaloConfirm.confirmButton")
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function Row({
  label,
  value,
}: {
  label: string;
  value: string;
}): JSX.Element {
  return (
    <div className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
      <span className="text-xs sm:text-sm text-muted-foreground shrink-0 sm:w-40">
        {label}
      </span>
      <span className="text-sm font-medium text-foreground break-words">
        {value}
      </span>
    </div>
  );
}
